import type { CorTabuleiro, Preferencias, Tema } from '../types';
import { tocarSom } from '../lib/audio';
import { Icone } from './Icone';

interface Props {
  preferencias: Preferencias;
  aoAlterar: (valor: Preferencias) => void;
  aoFechar: () => void;
}

const temas: Array<{ id: Tema; nome: string; descricao: string }> = [
  { id: 'regio', nome: 'Salão Régio', descricao: 'Nogueira, marfim e latão envelhecido' },
  { id: 'noturno', nome: 'Ébano Noturno', descricao: 'Grafite, bronze e contraste profundo' },
  { id: 'claro', nome: 'Galeria Clara', descricao: 'Carvalho, papel e metal acetinado' }
];

const coresTabuleiro: Array<{ id: CorTabuleiro; nome: string; descricao: string }> = [
  { id: 'nogueira', nome: 'Nogueira', descricao: 'Madeira clássica, casas médias' },
  { id: 'areia', nome: 'Areia', descricao: 'Bege e caramelo claros' },
  { id: 'floresta', nome: 'Floresta', descricao: 'Verde campo, ótimo contraste' },
  { id: 'oceano', nome: 'Oceano', descricao: 'Azul acinzentado suave' },
  { id: 'ardosia', nome: 'Ardósia', descricao: 'Cinza frio e limpo' },
  { id: 'contraste', nome: 'Alto contraste', descricao: 'Máxima leitura das pretas' }
];

export function PreferenciasPainel({ preferencias, aoAlterar, aoFechar }: Props) {
  const alterar = <K extends keyof Preferencias>(chave: K, valor: Preferencias[K]) =>
    aoAlterar({ ...preferencias, [chave]: valor });
  const corAtual = preferencias.corTabuleiro ?? 'nogueira';

  return (
    <div
      className="sobreposicao"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <section className="painel-preferencias" role="dialog" aria-modal="true" aria-labelledby="titulo-preferencias">
        <header>
          <div>
            <span>AMBIENTE</span>
            <h2 id="titulo-preferencias">Preferências</h2>
          </div>
          <button type="button" className="botao-icone" onClick={aoFechar} aria-label="Fechar preferências">
            <Icone nome="fechar" />
          </button>
        </header>

        <div className="grupo-preferencia">
          <h3>Aparência</h3>
          <div className="opcoes-tema">
            {temas.map((tema) => (
              <button
                type="button"
                key={tema.id}
                className={preferencias.tema === tema.id ? 'tema-opcao tema-ativo' : 'tema-opcao'}
                onClick={() => alterar('tema', tema.id)}
              >
                <span className={`amostra-tema amostra-${tema.id}`}>
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{tema.nome}</strong>
                <small>{tema.descricao}</small>
              </button>
            ))}
          </div>
        </div>

        <div className="grupo-preferencia">
          <h3>Cores do tabuleiro</h3>
          <p className="dica-preferencia">Independente do tema da interface — escolha o contraste que preferir para as peças.</p>
          <div className="opcoes-tabuleiro" role="listbox" aria-label="Cores do tabuleiro">
            {coresTabuleiro.map((cor) => (
              <button
                type="button"
                key={cor.id}
                role="option"
                aria-selected={corAtual === cor.id}
                className={corAtual === cor.id ? 'tabuleiro-opcao tabuleiro-ativo' : 'tabuleiro-opcao'}
                onClick={() => alterar('corTabuleiro', cor.id)}
              >
                <span className={`amostra-tabuleiro amostra-tabuleiro-${cor.id}`} aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{cor.nome}</strong>
                <small>{cor.descricao}</small>
              </button>
            ))}
          </div>
        </div>

        <div className="grupo-preferencia">
          <h3>Som e movimento</h3>
          <label className="linha-interruptor">
            <span>
              <strong>Sons da partida</strong>
              <small>Movimento, captura, xeque e resultado</small>
            </span>
            <input type="checkbox" checked={preferencias.somAtivo} onChange={(e) => alterar('somAtivo', e.target.checked)} />
            <i />
          </label>
          <label className="controle-volume">
            <span>Volume</span>
            <input
              aria-label="Volume dos sons"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={preferencias.volume}
              onChange={(e) => alterar('volume', Number(e.target.value))}
            />
            <button type="button" className="botao-contorno" onClick={() => tocarSom('movimento', preferencias.volume, true)}>
              Ouvir
            </button>
          </label>
          <label className="linha-interruptor">
            <span>
              <strong>Reduzir movimentos</strong>
              <small>Remove transições decorativas</small>
            </span>
            <input
              type="checkbox"
              checked={preferencias.reduzirMovimento}
              onChange={(e) => alterar('reduzirMovimento', e.target.checked)}
            />
            <i />
          </label>
          <label className="linha-interruptor">
            <span>
              <strong>Mostrar casas possíveis</strong>
              <small>Indica destinos legais após selecionar uma peça</small>
            </span>
            <input
              type="checkbox"
              checked={preferencias.mostrarDestinos}
              onChange={(e) => alterar('mostrarDestinos', e.target.checked)}
            />
            <i />
          </label>
        </div>

        <footer>
          <button type="button" className="botao-principal" onClick={aoFechar}>
            Concluir
          </button>
        </footer>
      </section>
    </div>
  );
}
